// FlashCardQuizApp_v01.tsx
// Mobile-first flash card quiz app
// Version: v01

import React, { useMemo, useState } from "react";
import { Container, Row, Col, Card, Button, Form, Badge, ProgressBar } from "react-bootstrap";
import { flashCardTopics_v01 } from "./QuizAppData_v01";

const FlashCardQuizApp_v01 = () => {
  const [topicId, setTopicId] = useState(flashCardTopics_v01[0]?.id ?? "");
  const [cardIndex, setCardIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);

  const selectedTopic = useMemo(
    () => flashCardTopics_v01.find((topic) => topic.id === topicId) ?? flashCardTopics_v01[0],
    [topicId]
  );

  const cards = selectedTopic?.cards ?? [];
  const currentCard = cards[cardIndex];

  const progress = cards.length ? ((cardIndex + 1) / cards.length) * 100 : 0;

  const changeTopic = (newTopicId: string) => {
    setTopicId(newTopicId);
    setCardIndex(0);
    setShowAnswer(false);
  };

  const nextCard = () => {
    setCardIndex((current) => (current + 1) % cards.length);
    setShowAnswer(false);
  };

  const previousCard = () => {
    setCardIndex((current) => (current === 0 ? cards.length - 1 : current - 1));
    setShowAnswer(false);
  };

  return (
    <section className="py-4 py-md-5 bg-light min-vh-100">
      <Container>
        <Row className="justify-content-center">
          <Col xs={12} lg={8}>
            <div className="mb-4 text-center">
              <Badge bg="primary" className="mb-2">Flash Card Quiz</Badge>
              <h1 className="fw-bold">Mobile-First Learning Deck</h1>
              <p className="text-muted mb-0">
                Select a topic, flip the card, and move through the deck.
              </p>
            </div>

            <Card className="border-0 shadow-sm rounded-4 mb-3">
              <Card.Body className="p-3 p-md-4">
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Choose Topic</Form.Label>
                  <Form.Select
                    value={topicId}
                    onChange={(e) => changeTopic(e.target.value)}
                  >
                    {flashCardTopics_v01.map((topic) => (
                      <option key={topic.id} value={topic.id}>
                        {topic.title}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <div className="text-muted small mb-3">
                  {selectedTopic?.description}
                </div>

                <ProgressBar now={progress} className="mb-3" />

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span className="small text-muted">
                    Card {cardIndex + 1} of {cards.length}
                  </span>
                  <Badge bg={showAnswer ? "success" : "secondary"}>
                    {showAnswer ? "Answer" : "Question"}
                  </Badge>
                </div>

                <Card className="rounded-4 border bg-white">
                  <Card.Body
                    className="d-flex align-items-center justify-content-center text-center p-4"
                    style={{ minHeight: "220px" }}
                  >
                    <div className="fs-4 fw-semibold">
                      {showAnswer ? currentCard?.back : currentCard?.front}
                    </div>
                  </Card.Body>
                </Card>

                <div className="d-grid gap-2 mt-3">
                  <Button
                    variant={showAnswer ? "outline-primary" : "primary"}
                    size="lg"
                    onClick={() => setShowAnswer((value) => !value)}
                  >
                    {showAnswer ? "Show Question" : "Show Answer"}
                  </Button>

                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-secondary"
                      className="w-50"
                      onClick={previousCard}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline-secondary"
                      className="w-50"
                      onClick={nextCard}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm rounded-4">
              <Card.Body className="p-3">
                <div className="fw-semibold mb-2">Available Topics</div>
                <div className="d-flex flex-wrap gap-2">
                  {flashCardTopics_v01.map((topic) => (
                    <Button
                      key={topic.id}
                      size="sm"
                      variant={topic.id === topicId ? "primary" : "outline-primary"}
                      onClick={() => changeTopic(topic.id)}
                    >
                      {topic.title}
                    </Button>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default FlashCardQuizApp_v01;

// End of FlashCardQuizApp_v01.tsx
// Version: v01